import { Box, Button, Icon, Tab, Tabs, Typography } from "@outschool/backpack";
import {
  SectionManageToolbar_ActivityFragment,
  SectionManageToolbar_SectionFragment,
} from "@outschool/gql-frontend-generated";
import { faMoon } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  pathnameWithoutLocale,
  sectionManageEnrollmentPath,
  sectionMeetingsPath,
  sectionNoteMakerPath,
} from "@outschool/routes";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { Subscript } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import * as Activity from "../../../shared/Activity";
import useSectionString from "../../../shared/hooks/useSectionString";
import * as Routes from "../../../shared/Routes";
import { useEducatorClassroomBreadcrumbs } from "../../hooks/Breadcrumbs";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import ActivityPrivacyStatusLabel from "../ActivityPrivacyStatusLabel";
import Link from "../Link";
import Toolbar, { CrumbArrow } from "../Toolbar";

export function BreadcrumbTrail({
  breadcrumbs,
}: {
  breadcrumbs: { path: string; name: string }[];
}) {
  return (
    <Subscript>
      {breadcrumbs.map(({ path, name }, idx) => (
        <React.Fragment key={idx}>
          <Link to={path}>{name}</Link>
          <CrumbArrow />
        </React.Fragment>
      ))}
    </Subscript>
  );
}

type ToolbarTab = {
  label: string;
  to: string;
  value: string;
};

const useToolbarTabs = ({
  activity,
  section,
}: {
  activity?: SectionManageToolbar_ActivityFragment;
  section?: SectionManageToolbar_SectionFragment | null;
}): ToolbarTab[] => {
  const { t } = useTranslation("client\\components\\nav\\SectionManageToolbar");
  const { isAdmin } = useSession();

  if (!activity || !section) {
    return [];
  }

  const showLessonUI = !!section?.syllabus?.published_at;

  return [
    ...(activity.isClub
      ? [
          {
            label: t("Group"),
            to: Routes.clubPath(section.uid),
            value: "group",
          },
        ]
      : [
          {
            label: t("Classroom"),
            to: Routes.classroomPath(
              section,
              undefined,
              showLessonUI ? "announcements" : "all",
            ),
            value: "classroom",
          },
        ]),
    ...(showLessonUI
      ? [
          {
            label: t("Lessons"),
            to: Routes.lessonsPath(section),
            value: "lessons",
          },
          ...(section.assignmentClassPosts.length > 0
            ? [
                {
                  label: t("Assignments"),
                  to: Routes.sectionAssignmentsPath(section),
                  value: "assignments",
                },
              ]
            : []),
          {
            label: t("Post Management"),
            to: Routes.sectionPostManagementPath(section),
            value: "postManagement",
          },
        ]
      : []),
    {
      label:
        activity.hasTeacherSchedule || section.is_self_paced
          ? t("Learner")
          : t("Learners"),
      to: Routes.sectionLearnersPath(section),
      value: "learners",
    },
    ...(activity.hasTeacherSchedule
      ? [
          {
            label: t("Meetings"),
            to: sectionMeetingsPath(section.uid),
            value: "meetings",
          },
        ]
      : []),
    ...(!activity.isClub
      ? [
          {
            label: t("Recordings"),
            to: Routes.sectionRecordingsPath(section),
            value: "recordings",
          },
        ]
      : []),
    ...(activity.hasTeacherSchedule
      ? [
          {
            label: t("Note Maker"),
            to: sectionNoteMakerPath(section.uid),
            value: "noteMaker",
          },
        ]
      : []),
    ...(!activity.hasTeacherSchedule &&
    !section.is_self_paced &&
    (!activity.isClub || isAdmin)
      ? [
          {
            label: t("Edit"),
            to: Routes.sectionEditPath(section),
            value: "edit",
          },
        ]
      : []),
    ...(section.is_self_paced
      ? [
          {
            label: t("Manage Enrollment"),
            to: sectionManageEnrollmentPath(section.uid),
            value: "manageEnrollment",
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            label: "🎩 Admin",
            to: Routes.sectionAdminPath(section),
            value: "admin",
          },
        ]
      : []),
  ];
};

export default function SectionManageToolbar({
  activity,
  section,
  learner,
}: {
  activity?: SectionManageToolbar_ActivityFragment;
  section?: SectionManageToolbar_SectionFragment | null;
  learner?: { uid: string; name?: string | null } | null;
  containerClassName?: string;
}) {
  const { t } = useTranslation("client\\components\\nav\\SectionManageToolbar");
  const location = useLocation();
  const Link = useLinkComponent();
  const isMobile = useIsMobile();

  const { fullDurationString } = useSectionString();

  const { timeZone: userTimeZone } = useTimeZone();
  const { currentUser, currentUserIsLoading, isAdmin } = useSession();
  const breadcrumbs = useEducatorClassroomBreadcrumbs(activity);

  const tabs = useToolbarTabs({ activity, section });

  if (!activity || !section || (!currentUser && currentUserIsLoading)) {
    return null;
  }

  const enabled = section.currentUserCanManage || isAdmin;
  if (!enabled) {
    return null;
  }

  const activeTab = tabs.find(tab =>
    tab.to.startsWith(pathnameWithoutLocale(location.pathname)),
  );

  return (
    <Toolbar>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1024px",
          margin: "0 auto",
          padding: "0 1em",
        }}
      >
        <BreadcrumbTrail breadcrumbs={breadcrumbs} />
        <Typography
          variant="h2"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 16,
          }}
          gutterBottom
        >
          {activity.hasTeacherSchedule ? (
            learner?.name ?? t("learner")
          ) : (
            <>
              {fullDurationString({
                activity,
                section,
                nextOngoingMeeting: section.nextOngoingMeeting,
                userTimeZone,
              })}
              {!Activity.isFlexSchedule(activity) && section.startsAtNight && (
                <Icon icon={faMoon} />
              )}
              <ActivityPrivacyStatusLabel section={section} />
              {activity && (
                <Button
                  variant="text"
                  component={Link}
                  to={Routes.activityPath(activity)}
                >
                  {t("view listing")}
                </Button>
              )}
            </>
          )}
        </Typography>

        <Tabs
          value={activeTab?.value || false}
          variant={isMobile ? "scrollable" : undefined}
        >
          {tabs.map(({ value, label, to }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              component={Link}
              to={to}
            />
          ))}
        </Tabs>
      </Box>
    </Toolbar>
  );
}

SectionManageToolbar.fragments = {
  activity: gql`
    fragment SectionManageToolbar_activity on Activity {
      uid
      user_uid
      title
      titleTranslated
      is_ongoing_weekly
      weekly_meetings
      duration_weeks
      isClub
      published_at
      hasTeacherSchedule
    }
  `,
  section: gql`
    fragment SectionManageToolbar_section on Section {
      uid
      published_at
      deleted_at
      canceled_at
      start_time
      end_time
      startsAtNight
      is_self_paced
      currentUserCanManage
      nextOngoingMeeting {
        uid
        start_time
      }
      assignmentClassPosts {
        uid
      }
      syllabus {
        uid
        published_at
      }
    }
  `,
};
